import {
  IAuditionTaskItemSession,
  IAuditionTaskSession,
  IPlayAuditionTask,
  tAuditionAnswer
} from 'common/modules/ReactShared';
import { t } from 'i18n';
import { useCallback, useEffect } from 'react';
import { usePromptContext } from 'students/views/shared/hooks';
import { customMediaQuery } from 'students/views/shared/styled';
import styled from 'styled-components';
import Streak from '../../common/components/Streak';
import { usePlayAnswers, useStreakFactor } from '../../common/hooks';
import PlayTaskScreenSwitcher from '../../common/screens/PlayTaskScreenSwitcher';
import { IPlayTaskProps, tPlayTaskComponentType } from '../types';
import PlayAuditionMainScreen from './PlayAuditionMainScreen';
import bgImage from './assets/AuditionTaskBg.png';
import bgImageWebp from './assets/AuditionTaskBg.webp';
import { ISelectAnswerPayload } from './components/AuditionItem';

const PlayAuditionTask: tPlayTaskComponentType<
  IPlayTaskProps<IPlayAuditionTask, IAuditionTaskSession>
> = ({ task, taskSession, isCompleting, onFinish, onNext, onAnswer, className }) => {
  const { items } = task;
  const answersFn = usePlayAnswers<tAuditionAnswer>();
  const { streakNumber, streakPercent, onTheFirstTry } = useStreakFactor();
  const { makeDirty } = usePromptContext();

  useEffect(() => {
    if (taskSession?.taskItemSessions) {
      answersFn(taskSession.taskItemSessions);
    } else {
      answersFn(
        items.map((item, index) => {
          return {
            id: index,
            taskItemId: item.id,
            completed: false,
            mistakesCount: 0,
            skipped: false,
            url: ''
          };
        })
      );
    }

    onTheFirstTry(false);
  }, [taskSession, items, onTheFirstTry, answersFn]);

  const updateAnswer = (itemId: number, data: Partial<tAuditionAnswer[number]>) => {
    const answerIndex = answersFn().findIndex(
      (answer: IAuditionTaskItemSession) => answer.taskItemId === itemId
    );

    const { mistakesCount, skipped } = data;

    if (answerIndex < 0) return;

    const answer = answersFn()[answerIndex];
    const newAnswerData = { ...answer, ...data };

    answersFn([
      ...answersFn().slice(0, answerIndex),
      newAnswerData,
      ...answersFn().slice(answerIndex + 1)
    ]);

    onAnswer && onAnswer(newAnswerData);

    onTheFirstTry(!(mistakesCount || skipped));
  };

  const handleSelectAnswer = (payload: ISelectAnswerPayload) => {
    const { itemId, mistakesCount } = payload;

    updateAnswer(itemId, {
      mistakesCount,
      skipped: false
    });

    makeDirty(true);
  };

  const handleSkip = (itemId: number) => {
    updateAnswer(itemId, {
      skipped: true
    });
  };

  const handleFinishTask = () => {
    onNext && onNext();
  };

  const submitTask = useCallback(() => {
    onFinish && onFinish(answersFn());
  }, [onFinish, answersFn]);

  const handleCompleteTask = (goToFinishScreen: () => void) => () => {
    submitTask();

    goToFinishScreen();
  };

  return (
    <>
      <Streak streakNumber={streakNumber} streakPercent={streakPercent} />
      <PlayTaskScreenSwitcher
        isCompleting={isCompleting}
        onExitFinishScreen={handleFinishTask}
        className={className}
        startScreenHeading={
          task.instruction ||
          t('frontend.lesson_task.tasks.play.audition.start_screen.heading')
        }
        itemsExist={!!items.length}
        taskStatus={taskSession?.status}
        completeTask={submitTask}
        taskColor="#491564"
      >
        {(goToFinishScreen) => (
          <PlayAuditionMainScreen
            task={task}
            onSelectAnswer={handleSelectAnswer}
            onComplete={handleCompleteTask(goToFinishScreen)}
            onSkip={handleSkip}
          />
        )}
      </PlayTaskScreenSwitcher>
    </>
  );
};

export default PlayAuditionTask;

PlayAuditionTask.backgroundComponent = styled.div`
  background: linear-gradient(180deg, #9f599d 0%, #491564 100%);

  ${customMediaQuery('tablet')} {
    background-repeat: no-repeat, no-repeat;
    background-position: center;
    background-size: cover, auto;
    background-image: url(${bgImage}), linear-gradient(180deg, #9f599d 0%, #491564 100%),
      linear-gradient(180deg, #26989b 0%, #82cdaf 100%),
      linear-gradient(180deg, #c7367d 0%, #e467a2 100%);

    .webp & {
      background-image: url(${bgImageWebp}),
        linear-gradient(180deg, #9f599d 0%, #491564 100%),
        linear-gradient(180deg, #26989b 0%, #82cdaf 100%),
        linear-gradient(180deg, #c7367d 0%, #e467a2 100%);
    }
  }
`;
PlayAuditionTask.bgColor = '#491564';
